import { Form, Input } from 'antd';
import Modal from 'components/Modal';
import { CustomerSearchInput } from 'features/appointment/pages/AppointmentsPage/components/Calendar/TableView/Body/Modal/QuickBookingV2/Customer';
import reportApis from 'features/report/services/apis';
import { IStaffAdjustmentSaleBody } from 'features/report/services/types/api';

import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { ICustomerItemData } from 'services/shop/types/customer';
import { IStaffItemData } from 'services/shop/types/staff';
import styled from 'styled-components';
import toast from 'utils/toast';
import StaffSearchInput from 'widgets/Staff/StaffSearchInput';
import AdjustSaleTransactionsList from './AdjustSaleTransactionsList';
import colorTheme from 'constants/color';
import DollarAmountInput from 'components/DollarAmount/Input';
import Text from 'components/Text';

type Props = {};

type HomeSaleAdjustmentModalRef = {
  open: (staffId?: string) => void;
  close: () => void;
};

type SaleAdjustmentFormValues = {
  amount: number;
  billId: string;
  staffIdIncrease: string;
  staffIdDecrease: string;
  note: string;
  customer: ICustomerItemData;
};
export const useHomeSaleAdjustmentModal = () => useRef<HomeSaleAdjustmentModalRef>(null);
const HomeSaleAdjustmentModal = forwardRef<HomeSaleAdjustmentModalRef, Props>((_props, ref) => {
  const [open, setOpen] = useState(false);
  const [createdById, setCreatedById] = useState<string>();

  const [form] = Form.useForm<SaleAdjustmentFormValues>();

  const onOpen: HomeSaleAdjustmentModalRef['open'] = (staffId) => {
    setCreatedById(staffId);
    setOpen(true);
  };

  const onClose = () => {
    form.resetFields();
    setOpen(false);
  };

  useImperativeHandle(ref, () => ({
    open: onOpen,
    close: onClose,
  }));

  const handleSubmit = async (values: any) => {
    const body: IStaffAdjustmentSaleBody = {
      amount: values.amount,
      billId: values.billId,
      staffIdIncrease: values.staffIdIncrease,
      staffIdDecrease: values.staffIdDecrease,
      note: values.note,
      createdBy: createdById,
    };

    try {
      const res = await reportApis.createSaleAdjustment(body);
      if (res?.data?.data) {
        toast.success('Create Adjust sale successfully');
        onClose();
      }
    } catch (error) {
      toast.error('Create Adjust sale failed');
    }
  };

  return (
    <Modal
      visible={open}
      onClose={onClose}
      width={1200}
      v2
      modalTitle='Sale Adjustment'
      noneBodyStyle={true}
      onSubmit={() => form.submit()}
      okTitle='OK'
    >
      <Container>
        <FormStyled layout='vertical' form={form} initialValues={{ amount: 0, billId: null }} onFinish={handleSubmit}>
          <LeftContent>
            <Form.Item noStyle name='customer'>
              <CustomerSearchInput
                v2NewApt
                preventUnknown
                autoOpenAddWhenNotFound
                v2
                onChange={(data) => {
                  form.setFieldValue('customer', data || null);
                }}
              />
            </Form.Item>
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => {
                const customer = getFieldValue('customer');
                const billId = getFieldValue('billId');
                const billIdError = form.getFieldError('billId');

                return customer?.id ? (
                  <AdjustSaleTransactionsList
                    customerId={customer.id}
                    onChooseTransaction={(transaction) => {
                      form.setFieldValue('billId', transaction.billId);
                      form.validateFields(['billId']);
                    }}
                    value={billId}
                    error={billIdError}
                  />
                ) : billIdError?.length > 0 ? (
                  <Text variant='H7' color='error_4' style={{ fontWeight: 500 }}>
                    Please select a customer
                  </Text>
                ) : (
                  <Text variant='H7' color='text_4_v2' style={{ fontWeight: 500 }}>
                    Please select a customer to see the transactions
                  </Text>
                );
              }}
            </Form.Item>
            <Form.Item name='billId' rules={[{ required: true, message: 'Please select a ticket' }]} hidden>
              <Input />
            </Form.Item>
          </LeftContent>
          <RightContent>
            <Form.Item
              name='amount'
              label='Amount'
              rules={[
                {
                  required: true,
                  message: 'Please enter amount',
                },
                {
                  validator: (_, value) => {
                    if (value > 0) return Promise.resolve();
                    return Promise.reject(new Error('Amount must be greater than 0'));
                  },
                },
              ]}
            >
              <DollarAmountInput className='input-dollar' withForm />
            </Form.Item>
            <Form.Item
              name='staffIdIncrease'
              label='Staff Sales Increase'
              rules={[{ required: true, message: 'Please choose a staff' }]}
            >
              <StaffSearchInput
                v2
                onPickAll={() => undefined}
                onPickStaff={(data: IStaffItemData | null) => {
                  form.setFieldValue('staffIdIncrease', data?.id);
                  form.validateFields(['staffIdIncrease']);
                }}
                showAllOption={false}
                sizeAvatar={40}
              />
            </Form.Item>
            <Form.Item
              name='staffIdDecrease'
              label='Staff Sales Decrease'
              rules={[{ required: true, message: 'Please choose a staff' }]}
            >
              <StaffSearchInput
                v2
                onPickAll={() => undefined}
                onPickStaff={(data: IStaffItemData | null) => {
                  form.setFieldValue('staffIdDecrease', data?.id);
                  form.validateFields(['staffIdDecrease']);
                }}
                showAllOption={false}
                sizeAvatar={40}
              />
            </Form.Item>
            <Form.Item name='note' label='Note'>
              <Input.TextArea rows={4} />
            </Form.Item>
          </RightContent>
        </FormStyled>
      </Container>
    </Modal>
  );
});

HomeSaleAdjustmentModal.displayName = 'HomeSaleAdjustmentModal';
export default HomeSaleAdjustmentModal;

const Container = styled.div`
  display: flex;
  width: 100%;
  max-height: 70vh;
  overflow-y: hidden;
  gap: 24px;
`;

const FormStyled = styled(Form)`
  width: 100%;
  background: #f5f5f5;
  margin-bottom: 6px;
  display: flex;
`;

const LeftContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 16px;
  overflow-y: auto;

  .ant-row {
    margin: 0 !important;
  }
  .container-input {
    padding-left: 0;
  }
`;

const RightContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 16px;

  .input-dollar {
    height: 3.5rem;
    .fake-labels {
      font-size: 20px!important;
      font-weight: 500;
      font-family: Poppins;
    }
  }

  .ant-form-item {
    margin-bottom: 0;
  }

  .ant-form-item-label > label {
    font-weight: 500;
    font-size: 22px;
    font-family: Poppins;
    color: ${colorTheme.text_4_v2};
  }

  .ant-input {
    font-size: 20px!important;
    font-weight: 500;
    font-family: Poppins;
  }
`;
